<template>
  <div
    :key="storeId"
    class="child"
  >
    <Meta
      property="pagetype"
      content="Unterkategorie"
    />
    <div class="wrapper-breadcrumb">
      <NavigationBreadcrumb :category-list="combinedCategoryTree" />
    </div>
    <div class="search-page">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-2">
        <aside class="lg:col-span-2 filter-widget flex flex-col relative">
          <Title>{{ categoryPageTitle }}</Title>
          <Meta
            name="description"
            :content="currentCategory?.contextSensitiveData?.seoDescription"
          />
          <SearchCategoryTree
            class="navigation-category-tree hidden lg:block"
            :class="categoryFlyout ? 'filter-flyout' : ''"
            :main-category="currentCategory"
            :category-tree="parentCategoryTree"
          />
        </aside>
        <section class="lg:col-span-10 search-results lg:border-l lg:border-solid lg:border-secondary">
          <LayoutCategoryHeader
            v-if="currentCategory"
            :current-category="currentCategory"
          />
          <div v-if="marketingCookie">
            <ClientOnly fallback-tag="div">
              <SearchSponsored
                v-if="parentCategory && currentCategory"
                :page-type="PageType.CATEGORY"
                :category-ids="[parentCategory.categoryId, currentCategory.categoryId]"
              >
                <template #default="{ sponsoredItems, onViewBeaconLink }">
                  <basic-ppm
                    v-if="sponsoredItems?.length > 0"
                    class="p-4"
                    :slides="sponsoredItems.map(mapArticleSlideFromSponsoredArticleHit)"
                    :list-name="'Highlights'"
                    :view-beacon="onViewBeaconLink"
                    is-sponsored
                  ></basic-ppm>
                </template>
                <template #placeholder>
                  <BasicPpmSkeleton
                    class="p-4"
                    :list-name="'Highlights'"
                    :link-to-more="true"
                  />
                </template>
              </SearchSponsored>
              <template #fallback>
                <BasicPpmSkeleton
                  class="p-4"
                  :list-name="'Highlights'"
                  :link-to-more="true"
                />
              </template>
            </ClientOnly>
          </div>
          <div
            v-for="category in combinedCategoryTree?.[1]?.descendants?.slice(0, 3) ?? []"
            :key="category.extId"
          >
            <SearchCategory
              v-if="category.extId"
              :category-ids="[category.extId]"
              :transform-items="mapToArticleSliders"
            >
              <template #default="{ items }">
                <basic-ppm
                  class="p-4"
                  :slides="items"
                  :list-name="`Bestseller in ${category?.name}`"
                  :link-to-more="`${$route.path.replace(/\/$/, '')}/${category.url.replace(/^\//, '')}`"
                />
              </template>
              <template #placeholder>
                <BasicPpmSkeleton
                  class="p-4"
                  :list-name="`Bestseller in ${category?.name}`"
                  :link-to-more="true"
                />
              </template>
            </SearchCategory>
          </div>
          <BasicLegalGuaranteeLink class="px-4 text-right" />
          <LayoutCategoryFooter
            v-if="currentCategory"
            :current-category="currentCategory"
          />
        </section>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { AisArticle, NeoArticleCategoryView } from '@expert/types';
import { getExpNavigationTree } from '~/composables/ExpApi/expCache';
import { mapArticleSlideFromHit, mapArticleSlideFromSponsoredArticleHit } from '~/composables/Mapper/ArticleSlide';
import { useCategoryFlyoutState } from '~/composables/states';
import type { MenuLink } from '~~/models/Navigation';

const storeId: Ref<string | null> = computed(() => useCurrentStore()?.value?.id || useStoreCookie().value);

const { data: navigationTree } = getExpNavigationTree('MAIN');

const route = useRoute();
const categoryFlyout = useCategoryFlyoutState();
const marketingCookie = getCookiePermissions(PermissionMapping.MARKETING);

const parentCategoryTree: ComputedRef<Array<MenuLink>> = computed(() => {
  const parentCategoryFromTree = navigationTree.value?.find(
    (menuLink) => menuLink.url === useRoute().params.parentCategory,
  );
  return parentCategoryFromTree ? [parentCategoryFromTree] : [];
});

const combinedCategoryTree: ComputedRef<Array<MenuLink>> = computed(() => {
  const currentCategoryFromTree = parentCategoryTree.value?.[0]?.descendants?.find(
    (menuLink) => menuLink.url === useRoute().params.childCategory,
  );
  return currentCategoryFromTree ? parentCategoryTree.value.concat(currentCategoryFromTree) : parentCategoryTree.value;
});

const { data: parentCategory } = await useFetch<NeoArticleCategoryView>('/api/neo/article/categories', {
  query: {
    slug: route.params.parentCategory,
  },
  key: `categories?categoryId=${route.params.parentCategory}`,
});

const { data: currentCategory } = await useFetch<NeoArticleCategoryView>('/api/neo/article/categories', {
  query: {
    slug: route.params.childCategory,
  },
  key: `categories?categoryId=${route.params.childCategory}`,
});

const categoryPageTitle = computed(() => {
  return getCategoryPageTitle(currentCategory.value?.contextSensitiveData);
});

const mapToArticleSliders = (hits: Array<AisArticle>): Array<ArticleSlide> => {
  if (hits && hits.length > 0) {
    setSeoPrimaryImage(hits[0], currentCategory.value);
  }

  return hits.map(mapArticleSlideFromHit);
};

const schemaOrgBuilder = new SchemaOrgBuilder();
const setSeoPrimaryImage = async (product: AisArticle, category: any): Promise<void> => {
  schemaOrgBuilder.withPrimaryImageOfPage(
    await setMetaProductData(
      product,
      category?.categoryId,
      category?.contextSensitiveData?.seoTitle ?? category?.contextSensitiveData?.name,
    ),
  );
  useSchemaOrg([
    defineWebPage({
      primaryImageOfPage: {
        '@id': 'https://www.expert.de/#primaryImage',
      },
      about: {
        '@id': 'https://www.expert.de/#organization',
      },
      inLanguage: 'de-DE',
      image: {
        '@id': 'https://www.expert.de/#primaryImage',
      },
    }),
    schemaOrgBuilder.buildPrimaryImage(),
  ]);
};

if (import.meta.client) {
  window.scrollTo(0, 0);
}

useTrickOnce('VIEW_CATEGORY', {
  content: currentCategory?.value?.categoryId,
});
</script>
